import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'

import { authClient, logout } from '../lib/firebase'
import {
	SCRATCHPAY_CASH_BACKEND_CONFIG,
	CHECKOUT_PAYMENT_SERVICE_CONFIG,
	CHECKOUT_COMMS_SERVICE_CONFIG,
	CONTEXT_SERVICE_CONFIG
} from '../config'
import { constructAuthHeader } from '../util/api-helpers'
import { reportError } from 'observability/reportError'

const interceptRequest = async (config) => {
	const authHeader = await constructAuthHeader()
	return {
		...config,
		headers: {
			...config.headers,
			Authorization: authHeader
		}
	}
}

const constructCheckoutPaymentAndCommsServiceAuthHeader = async () => {
	const accessToken = authClient().currentUser
		? await authClient().currentUser?.getIdToken()
		: undefined
	return `Token ${accessToken}`
}

const interceptCheckoutPaymentAndCommsServiceRequest = async (config) => {
	const authHeader = await constructCheckoutPaymentAndCommsServiceAuthHeader()
	return {
		...config,
		headers: {
			...config.headers,
			Authorization: authHeader
		}
	}
}

// A relative url tells us nothing in Grafana, and the status is the first thing anyone filters on
const describeRequest = (error) => ({
	source: 'axios' as const,
	url: `${error.config?.baseURL ?? ''}${error.config?.url ?? ''}` || undefined,
	status: error.response?.status
})

const intercepResponseError = async (error) => {
	reportError(error, describeRequest(error))

	const status = error.response?.status
	if (status === 401 || status === 403) {
		logout()
	}

	return Promise.reject(error)
}

// We use (response)=>response.data.  It's not the best style, but this is how you can make it DTRT and not require .data
export type AxiosClientDirect = Omit<
	AxiosInstance,
	| 'request'
	| 'get'
	| 'delete'
	| 'head'
	| 'options'
	| 'post'
	| 'put'
	| 'patch'
	| 'postForm'
	| 'putForm'
	| 'patchForm'
> & {
	request<T = any, D = any>(config: AxiosRequestConfig<D>): T
	get<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): T
	delete<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): T
	head<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): T
	options<T = any, D = any>(url: string, config?: AxiosRequestConfig<D>): T
	post<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
	put<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
	patch<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
	postForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
	putForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
	patchForm<T = any, D = any>(url: string, data?: D, config?: AxiosRequestConfig<D>): T
}

const axiosClientRaw = axios.create({
	baseURL: SCRATCHPAY_CASH_BACKEND_CONFIG.baseURL,
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json'
	}
})
axiosClientRaw.interceptors.request.use(interceptRequest)
axiosClientRaw.interceptors.response.use((response) => response.data, intercepResponseError)
const axiosClient: AxiosClientDirect = axiosClientRaw as AxiosClientDirect

const checkoutPaymentServiceAxiosClientRaw = axios.create({
	baseURL: CHECKOUT_PAYMENT_SERVICE_CONFIG.baseURL,
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json'
	}
})
checkoutPaymentServiceAxiosClientRaw.interceptors.request.use(
	interceptCheckoutPaymentAndCommsServiceRequest
)
checkoutPaymentServiceAxiosClientRaw.interceptors.response.use(
	(response) => response.data,
	intercepResponseError
)
const checkoutPaymentServiceAxiosClient: AxiosClientDirect =
	checkoutPaymentServiceAxiosClientRaw as AxiosClientDirect

const checkoutCommsServiceAxiosClientRaw = axios.create({
	baseURL: CHECKOUT_COMMS_SERVICE_CONFIG.baseURL,
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json'
	}
})
checkoutCommsServiceAxiosClientRaw.interceptors.request.use(
	interceptCheckoutPaymentAndCommsServiceRequest
)
checkoutCommsServiceAxiosClientRaw.interceptors.response.use(
	(response) => response.data,
	intercepResponseError
)
const checkoutCommsServiceAxiosClient: AxiosClientDirect =
	checkoutCommsServiceAxiosClientRaw as AxiosClientDirect

/**
 * Get a downloadable link from the backend.  Axios assumes the result is json, whereas we want the raw data here.
 * @param url the url to hit for the backend
 * @param body the JSON object to send along with the request
 * @param defaultFileName the defaultFilename to use if the filename isn't provided with the result.
 * @param corsRedirect if we expect the file to come from a google bucket, turn this on so it assumes the link returns a
 */
async function getDownloadLinkFromBackend(
	url: string,
	body: any,
	defaultFileName: string,
	corsRedirect: boolean = false
) {
	const { baseURL } = SCRATCHPAY_CASH_BACKEND_CONFIG
	const firstLink = constructAuthHeader().then((authHeader) =>
		fetch(`${baseURL}${url}`, {
			method: corsRedirect ? 'POST' : 'GET', // Assume if doing cors, we want the POST first
			referrerPolicy: 'origin',
			headers: {
				'Content-Type': 'application/json',
				Authorization: authHeader
			},
			body: corsRedirect ? JSON.stringify(body || {}) : undefined
		})
	)

	// If doing a corsRedirect, we need to get the response as text and then retrieve the url and then
	// redownload.  Cors doesn't send referrer on a redirect across domain, so we have to do it by hand.
	if (corsRedirect) {
		return firstLink.then(async (response: Response) => {
			// Get the url and then fetch that.
			if (!response.ok) {
				throw new Error(response.statusText)
			}
			const data = await response.json()

			const a = document.createElement('a')
			a.href = data.data.url
			a.download = defaultFileName
			a.click()
		})
	}

	// Assume it's less than 10MB and we can just stream it
	return firstLink
		.then((response) => response.blob())
		.then((blob) => {
			const a = document.createElement('a')
			a.href = window.URL.createObjectURL(blob)
			a.download = defaultFileName
			a.click()
		})
}

const contextServiceAxiosClientRaw = axios.create({
	baseURL: CONTEXT_SERVICE_CONFIG.baseURL,
	headers: {
		'Content-Type': 'application/json',
		Accept: 'application/json'
	}
})
contextServiceAxiosClientRaw.interceptors.request.use(interceptRequest)
contextServiceAxiosClientRaw.interceptors.response.use(
	(response) => response.data,
	intercepResponseError
)
const contextServiceAxiosClient: AxiosClientDirect =
	contextServiceAxiosClientRaw as AxiosClientDirect

export {
	checkoutPaymentServiceAxiosClient,
	checkoutCommsServiceAxiosClient,
	contextServiceAxiosClient,
	getDownloadLinkFromBackend
}

export default axiosClient
